/**
 * @file Browser-safe wire shapes for the cross-daemon settings
 *       surface. Imported by both the server-side `Settings` class
 *       in `../settings.ts` AND any admin SPA code that needs to
 *       validate or type a settings payload.
 * @module @labcraft/postgres/shared/settings
 *
 * Pure Zod — no node-only deps, nothing that would pull `pg` into
 * a SPA bundle. The actual class + Postgres-coupled DDL constants
 * live in `../settings.ts`; this module is the contract slice that
 * crosses the server/browser boundary.
 *
 * Imported via the package's `./shared` export so the bundler's
 * resolver never reaches into the root barrel (which transitively
 * imports `pg`). See `ts/lib/postgres/package.json` § `exports`.
 *
 * Authoring shape — each daemon publishes a plain
 * `z.object({...})` whose top-level fields carry operator-policy
 * metadata via Zod 4's `.meta({ key, label, description, default })`.
 * The TS handle (`waitlistLearners`) is the consumer-facing
 * identifier; the wire/storage `key` (`waitlist_learners`) is a
 * snake_case mirror declared in `.meta()`. Server-side
 * `describeSchema()` walks the schema and emits the JSON wire shape
 * the admin SPA consumes.
 */

import { z } from 'zod'

////////////////////////////////////////////////////////////////////////////////
// Authoring metadata
////////////////////////////////////////////////////////////////////////////////

/**
 * Operator-policy metadata attached to each settings field via Zod
 * 4's `.meta()`. The `default` here is the **operator's default for
 * the policy plane** — surfaced to the admin UI as the fallback
 * value when no `system_settings` row exists. It is NOT a Zod
 * parsing default; the underlying field schema stays required (per
 * `.agents/rules/design.md` § "Defaults belong to the operator, not
 * the schema").
 *
 * @property key - Wire/storage identifier (snake_case mirror of the
 *   TS handle). Stable across releases; rename requires a SQL
 *   migration of `system_settings` history.
 * @property label - Short operator-facing label for the row.
 * @property description - One-paragraph help text, plain prose.
 *   Shown beneath the control in the admin UI.
 * @property default - Value the daemon uses when no operator write
 *   exists. Inferred from the schema field's `z.output<>` type so
 *   `.meta({ default: 'yes' })` on a `z.boolean()` field is a
 *   compile-time error.
 */
export type SettingMeta<T = unknown> = {
  key: string
  label: string
  description: string
  default: T
}

/**
 * Read `SettingMeta` from a Zod field. Throws if the field wasn't
 * authored with all four required keys. Used by the Settings class
 * to walk schemas at construction time and by `describeSchema()` to
 * emit wire data.
 */
export function readSettingMeta(field: z.ZodType): SettingMeta {
  const m = (field.meta() ?? {}) as Partial<SettingMeta>
  if (
    typeof m.key !== 'string' ||
    typeof m.label !== 'string' ||
    typeof m.description !== 'string' ||
    m.default === undefined
  ) {
    throw new Error(
      `settings field is missing required .meta({ key, label, description, default })`,
    )
  }
  return {
    key: m.key,
    label: m.label,
    description: m.description,
    default: m.default,
  }
}

////////////////////////////////////////////////////////////////////////////////
// Wire shape (admin SPA contract)
////////////////////////////////////////////////////////////////////////////////

/**
 * Wire discriminator for the admin SPA's widget dispatch. Derived
 * server-side from each schema field's `_def.typeName`
 * (`ZodBoolean` → `'boolean'`, `ZodEnum` → `'enum'`, etc.) so the
 * client renders without having the Zod schema in its bundle.
 */
const SettingTypeSchema = z.enum([
  'boolean',
  'number',
  'string',
  'enum',
  'json',
])

/** Parsed `SettingType`. */
export type SettingType = z.infer<typeof SettingTypeSchema>

/**
 * Wire shape for one settings field. Produced server-side from a
 * Zod schema by `describeSchema()`; consumed by the admin SPA's
 * `/api/settings/schema` route. Keys / labels / descriptions /
 * defaults come from the field's `.meta()`; `type` and `enumValues`
 * are inferred from the underlying Zod type.
 *
 * @property key - Wire/storage identifier (snake_case).
 * @property label - Operator-facing label.
 * @property description - Operator-facing prose.
 * @property default - Operator-policy default (from `.meta()`).
 * @property type - Widget discriminator.
 * @property enumValues - Required when `type === 'enum'`; the
 *   allowed values for the select widget.
 */
export const SettingDefinitionSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  description: z.string().min(1),
  default: z.unknown(),
  type: SettingTypeSchema,
  enumValues: z.array(z.string()).optional(),
})

/** Parsed `SettingDefinition`. */
export type SettingDefinition = z.infer<typeof SettingDefinitionSchema>

////////////////////////////////////////////////////////////////////////////////
// Schema → wire serializer
////////////////////////////////////////////////////////////////////////////////

/**
 * Walk a daemon's `z.object({...})` settings schema and emit the
 * flat wire array the admin SPA renders. One entry per top-level
 * field. Iteration order matches the schema's declaration order so
 * the admin UI's row order is deterministic.
 */
export function describeSchema(
  schema: z.ZodObject<z.ZodRawShape>,
): SettingDefinition[] {
  const out: SettingDefinition[] = []
  for (const field of Object.values(schema.shape)) {
    out.push(describeField(field as z.ZodType))
  }
  return out
}

/** Walk a single Zod field and emit its wire shape. */
export function describeField(field: z.ZodType): SettingDefinition {
  const meta = readSettingMeta(field)
  const widget = inferWidgetType(field)
  return {
    key: meta.key,
    label: meta.label,
    description: meta.description,
    default: meta.default,
    type: widget.type,
    ...(widget.enumValues ? { enumValues: widget.enumValues } : {}),
  }
}

/**
 * Strip optional / default / nullable / prefault wrappers to reach
 * the underlying type. Settings fields are required by design (per
 * `.agents/rules/design.md` § "Defaults belong to the operator,
 * not the schema") but the unwrap stays so a future operator-typed
 * field that uses `.optional()` for genuine three-state semantics
 * still dispatches correctly.
 */
function unwrap(field: z.ZodType): z.ZodType {
  let inner = field
  while (
    inner instanceof z.ZodOptional ||
    inner instanceof z.ZodDefault ||
    inner instanceof z.ZodPrefault ||
    inner instanceof z.ZodNullable
  ) {
    inner = inner.unwrap() as z.ZodType
  }
  return inner
}

/** Map a Zod field to the wire widget discriminator. */
function inferWidgetType(field: z.ZodType): {
  type: SettingType
  enumValues?: string[]
} {
  const inner = unwrap(field)
  if (inner instanceof z.ZodBoolean) return { type: 'boolean' }
  if (inner instanceof z.ZodNumber) return { type: 'number' }
  if (inner instanceof z.ZodEnum) {
    const values = Object.values(inner.enum).filter(
      (v): v is string => typeof v === 'string',
    )
    return { type: 'enum', enumValues: values }
  }
  if (inner instanceof z.ZodString) return { type: 'string' }
  return { type: 'json' }
}

////////////////////////////////////////////////////////////////////////////////
// History entry (returned by `Settings.history()` and the admin
// /api/settings/:key/history response)
////////////////////////////////////////////////////////////////////////////////

/**
 * One row of `system_settings` history, normalized to wire shape.
 *
 * @property value - The value at this point in history.
 * @property setAt - ISO-8601 timestamp the row was inserted.
 * @property setBy - UUID of the admin operator who wrote the row.
 */
export type SettingHistoryEntry = {
  value: unknown
  setAt: string
  setBy: string
}
